import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
};

// Catches a render-time crash anywhere below it and shows a calm fallback rather
// than a blank white screen. It is the last line of defence in the UI: an
// unexpected exception in a page should degrade to "something went wrong, reload"
// — never a raw stack trace, and never nothing at all.
//
// It does not catch errors from async work (a fetch rejection); those are
// handled where they happen, via `ApiRequestError`. This is only for the
// synchronous render path.
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface it to the console for local debugging; a real error reporter hooks
    // in here later.
    console.error("Unhandled UI error", error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-stone-50 p-6">
        <div className="max-w-sm text-center">
          <h1 className="text-lg font-semibold text-stone-900">
            Something went wrong
          </h1>
          <p className="mt-2 text-sm text-stone-600">
            The page ran into an unexpected problem. Reloading usually fixes it.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-6 rounded-lg bg-stone-900 px-4 py-2 text-sm font-medium text-white hover:bg-stone-700"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
